import { useTranslation } from 'react-i18next';

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  suggestions: string[];
}
const PasswordStrengthIndicator = ({ password, className = '' }: PasswordStrengthIndicatorProps) => {
  const { t } = useTranslation('auth');
  const calculateStrength = (pwd: string): PasswordStrength => {
    let score = 0;
    const suggestions: string[] = [];

    if (pwd.length === 0) {
      return {
        score: 0,
        label: '',
        color: '',
        suggestions: [],
      };
    }

    // Length check
    if (pwd.length >= 8) {
      score += 1;
    } else {
      suggestions.push(t('passwordStrength.suggestions.useAtLeast', { min: 8 }));
    }

    if (pwd.length >= 12) {
      score += 1;
    }

    // Character variety checks
    if (/[a-z]/.test(pwd)) {
      score += 1;
    } else {
      suggestions.push(t('passwordStrength.suggestions.includeLowercase'));
    }

    if (/[A-Z]/.test(pwd)) {
      score += 1;
    } else {
      suggestions.push(t('passwordStrength.suggestions.includeUppercase'));
    }

    if (/\d/.test(pwd)) {
      score += 1;
    } else {
      suggestions.push(t('passwordStrength.suggestions.includeNumbers'));
    }

    if (/[^a-zA-Z\d]/.test(pwd)) {
      score += 1;
    } else {
      suggestions.push(t('passwordStrength.suggestions.includeSpecial'));
    }

    // Common patterns (negative points)
    if (/(.)\1{2,}/.test(pwd)) {
      score -= 1;
      suggestions.push(t('passwordStrength.suggestions.avoidRepeating'));
    }

    if (/123|abc|qwe|password|admin/i.test(pwd)) {
      score -= 2;
      suggestions.push(t('passwordStrength.suggestions.avoidCommonPatterns'));
    }

    // Determine label and color
    let label = '';
    let color = '';

    if (score <= 1) {
      label = t('passwordStrength.labels.veryWeak');
      color = 'text-destructive'; // OKLCH red for weak passwords
    } else if (score <= 2) {
      label = t('passwordStrength.labels.weak');
      color = 'text-muted-foreground'; // OKLCH neutral for fair passwords
    } else if (score <= 3) {
      label = t('passwordStrength.labels.fair');
      color = 'text-primary'; // OKLCH teal for good passwords
    } else if (score <= 4) {
      label = t('passwordStrength.labels.good');
      color = 'text-primary'; // OKLCH teal for good passwords
    } else {
      label = t('passwordStrength.labels.strong');
      color = 'text-foreground'; // OKLCH dark for strong passwords
    }

    return {
      score: Math.max(0, Math.min(5, score)),
      label,
      color,
      suggestions: suggestions.slice(0, 3), // Show max 3 suggestions
    };
  };

  const strength = calculateStrength(password);

  if (!password) {
    return <div></div>;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Strength bar */}
      <div className='flex items-center space-x-2'>
        <span className='text-xs text-muted-foreground'>{t('passwordStrength.strength')}</span>
        <div className='flex-1 flex space-x-1'>
          {[1, 2, 3, 4, 5].map((level) => (
            <div key={level} className={`h-1 flex-1 rounded-full ${level <= strength.score ? (strength.score <= 1 ? 'bg-status-error' : strength.score <= 2 ? 'bg-status-warning' : strength.score <= 3 ? 'bg-status-info' : 'bg-status-success') : 'bg-muted'}`} />
          ))}
        </div>
        <span className={`text-xs font-medium ${strength.color}`}>{strength.label}</span>
      </div>

      {/* Suggestions */}
      {strength.suggestions.length > 0 && (
        <div className='text-xs text-muted-foreground'>
          <div className='font-medium mb-1'>{t('passwordStrength.suggestionsTitle')}</div>
          <ul className='space-y-1'>
            {strength.suggestions.map((suggestion, index) => (
              <li key={index} className='flex items-start space-x-1'>
                <span className='opacity-60'>•</span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
